<template>
  <div class="top-menu-wrapper">
    <!-- 모바일 메뉴 토글 버튼 (커스텀 버튼만 사용) -->
    <Button
      v-if="isMobile"
      icon="pi pi-bars"
      @click="toggleMenu"
      class="mobile-menu-button"
    />
    <!-- Menubar 컴포넌트에 모바일 상태 바인딩 -->
    <Menubar
      v-show="!isMobile || isMenuVisible"
      :model="menuItems"
      class="top-menu"
      :class="{ 'mobile-menu-active': isMenuVisible }"
      :aria-label="'Main Navigation'"
    >
      <!-- 기존 메뉴 아이템 템플릿 유지 -->
      <template #item="{ item, props }">
        <a
          v-if="item.to"
          :href="item.to"
          v-bind="props.action"
          @click="(e) => onMenuItemClick(item, e)"
          :aria-label="item.label"
          class="menu-item-link"
          :class="[
            { unauthorized: !item.authorized },
            { 'active-menuitem': item.class === 'active-menuitem' },
          ]"
          :tabindex="item.items ? -1 : 0"
          role="menuitem"
        >
          <span
            v-if="item.icon"
            :class="[item.icon, props.icon]"
            style="margin-right: auto"
            role="presentation"
          ></span>
          <span
            :class="props.label"
            class="menu-item-text"
          >
            {{ item.label }}
          </span>
          <span
            v-if="item.items"
            :class="getIconClass(item.level)"
            class="menu-item-arrow"
            role="presentation"
          ></span>
        </a>
        <a
          v-else
          v-bind="props.action"
          :aria-label="item.label"
          class="menu-item-link"
          :class="[
            { unauthorized: !item.authorized },
            { 'active-menuitem': item.class === 'active-menuitem' },
          ]"
          :tabindex="item.items ? -1 : 0"
          role="menuitem"
        >
          <span
            v-if="item.icon"
            :class="[item.icon, props.icon]"
            style="margin-right: auto"
            role="presentation"
          ></span>
          <span
            :class="props.label"
            class="menu-item-text"
          >
            {{ item.label }}
          </span>
          <span
            v-if="item.items"
            :class="getIconClass(item.level)"
            class="menu-item-arrow"
            role="presentation"
          ></span>
        </a>
      </template>
    </Menubar>
  </div>
</template>

<script setup>
  //권한이 없는 메뉴는 클릭할 수 없도록 되어 있습니다.
  import { useMenuStore } from '@/stores/menu';
  import { useRouter } from 'vue-router';
  import { useRoute } from 'vue-router'; // Add this
  import { useTabStore } from '@/stores/tab';
  import { useLayoutStore } from '@/stores/layout';
  import { useAuthStore } from '@/stores/auth';
  import { ref, watch, computed, onMounted, onUnmounted } from 'vue'; // Add ref and watch
  const authStore = useAuthStore();
  const menuStore = useMenuStore();
  const router = useRouter();
  const route = useRoute(); // Add this
  const tabStore = useTabStore();

  // Add active menu tracking
  const activeMenuPath = ref('');

  const isMobile = ref(false);
  const isMenuVisible = ref(false);

  const checkMobile = () => {
    isMobile.value = window.innerWidth < 992;
  };

  const toggleMenu = () => {
    isMenuVisible.value = !isMenuVisible.value;
  };

  onMounted(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', checkMobile);
  });

  // Watch for route changes
  watch(
    () => route.path,
    (newPath) => {
      console.log('New path:', newPath); // 디버깅
      activeMenuPath.value = newPath;
    },
    { immediate: true },
  );

  // 권한 체크 함수
  const checkAuthorization = (item) => {
    if (!item.to) return true;

    let route;
    try {
      route = router.resolve(item.to);
    } catch (error) {
      console.warn(`Invalid route: ${item.to}`);
      return true;
    }

    if (!route.meta || !route.meta.permission) return true;
    return authStore.hasAnyPermission(route.meta.permission);
  };

  // 메뉴 아이템 클릭 시 모바일 메뉴 닫기 추가
  const onMenuItemClick = (item, event) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    if (isMobile.value) {
      isMenuVisible.value = false; // 메뉴 아이템 클릭 시 메뉴 닫기
    }

    if (item.to) {
      const layoutStore = useLayoutStore();
      const currentLayout = layoutStore.layoutMode;
      if (layoutStore.useTabView) {
        tabStore.addTab({
          path: item.to,
          name: item.label,
          layout: currentLayout,
        });
      } else {
        router.push(item.to);
      }
    }
  };

  const getIconClass = (level) => {
    return level === 0 ? 'pi pi-chevron-down' : 'pi pi-chevron-right';
  };
  // 경로를 세그먼트로 분리하는 헬퍼 함수
  const getPathSegments = (path) => {
    return path.split('/').filter((segment) => segment.length > 0);
  };

  const isActivePathAtLevel = (itemPath, currentPath) => {
    const itemSegments = getPathSegments(itemPath);
    const currentSegments = getPathSegments(currentPath);

    // 현재 항목이 활성화된 경로의 일부인지 확인
    for (let i = 0; i < itemSegments.length; i++) {
      if (itemSegments[i] !== currentSegments[i]) {
        return false;
      }
    }
    return true;
  };

  const transformMenuItems = (items, level = 0) => {
    return items.map((item) => {
      const newItem = {
        ...item,
        level,
        authorized: checkAuthorization(item),
        class: '',
      };

      if (item.to && activeMenuPath.value) {
        // 현재 아이템이 활성화된 경로의 일부인지 확인
        const isPartOfActivePath = isActivePathAtLevel(
          item.to,
          activeMenuPath.value,
        );
        if (isPartOfActivePath) {
          newItem.class = 'active-menuitem';
          console.log(`Activating menu item: ${item.to}`); // 디버깅용
        }
      }

      if (item.items) {
        newItem.items = transformMenuItems(item.items, level + 1);
        
        // 하위 메뉴 중 하나라도 권한이 있으면 상위 메뉴 표시
        const hasAuthorizedChild = newItem.items.some(child => child.authorized);
        newItem.authorized = hasAuthorizedChild;
      }

      if (item.to) {
        const originalCommand = newItem.command;
        newItem.command = (commandEvent) => {
          if (!newItem.authorized) {
            if (commandEvent.originalEvent) {
              commandEvent.originalEvent.preventDefault();
            }
            alert('이 기능에 대한 접근 권한이 없습니다.');
            return;
          }
          onMenuItemClick(item, commandEvent.originalEvent);
          if (originalCommand) originalCommand(commandEvent);
        };
      }

      return newItem;
    }).filter(item => item.authorized); // 권한이 없는 상위 메뉴는 제거
  };

  //const menuItems = transformMenuItems(menuStore.menuItems);
  const menuItems = computed(() => {
    console.log(
      'Transformed menu items:',
      transformMenuItems(menuStore.menuItems),
    );
    return transformMenuItems(menuStore.menuItems);
  });
</script>

<style lang="scss" scoped>
  .top-menu-wrapper {
    width: 100%;
    background-color: var(--surface-card);
    border-bottom: 1px solid var(--surface-border);
    position: fixed;
    top: 4rem;
    left: 0;
    z-index: 996;

    @media screen and (max-width: 768px) {
      .top-menu {
        display: none; // 기본적으로 모바일에서 숨김
        &.mobile-menu-active {
          display: block !important;
          position: fixed;
          top: 4rem;
          left: 0;
          width: 100%;
          height: calc(100vh - 4rem);
          background-color: var(--surface-card);
          z-index: 995;
          overflow-y: auto;
          padding-top: 1rem;

          :deep(.p-menubar) {
            padding: 0;
            background: transparent;
            border: none;

            .p-menubar-root-list {
              display: flex;
              flex-direction: column; // 모바일에서 세로로 정렬
              height: auto !important;
              padding: 0 1rem;

              > .p-menuitem {
                width: 100%;
                margin: 0;

                .p-menuitem-link {
                  padding: 1rem;
                  height: auto !important;
                  min-height: 3rem;
                }
              }
            }
          }
        }
      }
    }
  }

  .mobile-menu-button {
    display: none;
    position: fixed;
    top: 4.2rem;
    right: 1rem;
    z-index: 997;

    @media screen and (max-width: 768px) {
      display: block;
    }
  }

  // 기존 데스크탑 스타일
  .top-menu :deep(.p-menubar-root-list) {
    height: 36px !important;
  }

  .top-menu :deep(.p-menuitem-item) {
    height: 36px !important;
    line-height: 36px !important;
  }

  .unauthorized {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background-color: transparent !important;

      .menu-item-text,
      .menu-item-arrow,
      .pi {
        color: var(--primary-200) !important;
      }
    }
  }

  .menu-item-link {
    display: flex;
    justify-content: space-between;
    align-items: center;
    outline: none;
    padding: 0.5rem 1rem;
    height: 36px;
    &:focus {
      outline: 2px solid var(--primary-color);
      outline-offset: -2px;
    }
    &:focus:not(:focus-visible) {
      outline: none;
    }
    &.active-menuitem {
      background-color: var(--primary-50);

      .menu-item-text {
        color: var(--primary-color) !important;
      }

      .menu-item-arrow {
        color: var(--primary-color) !important;
      }
    }
  }

  .menu-item-text {
    flex-grow: 1;
    text-align: left;
    margin-right: 1rem;
  }

  .menu-item-arrow {
    font-size: 0.5rem;
    margin-left: auto;
  }

  .p-menubar.p-component.top-menu {
    background-color: var(--surface-card);
    padding-left: 1.2rem;
    padding-top: 0.1rem;
    padding-bottom: 0.1rem;

    :deep(.p-menubar-root-list > .p-menuitem:first-child) {
      margin-left: 1rem;
    }

    :deep(.p-menuitem-link.active-menuitem) {
      background-color: var(--primary-50) !important;

      .menu-item-text {
        color: var(--primary-color) !important;
      }

      .menu-item-arrow {
        color: var(--primary-color) !important;
      }

      &:hover {
        background-color: var(--primary-100) !important;
      }
    }

    :deep(.menu-item-text) {
      color: var(--surface-0) !important;
    }

    .p-menuitem-link:hover {
      .menu-item-text {
        color: var(--primary-200) !important;
      }
    }
  }

  .top-menu {
    :deep(.p-menuitem-link.active-menuitem) {
      background-color: var(--primary-50) !important;
    }
    :deep(.p-menuitem) {
      &.active-menuitem,
      .active-menuitem {
        background-color: var(--primary-50) !important;

        .menu-item-text {
          color: var(--primary-color) !important;
        }

        .menu-item-arrow {
          color: var(--primary-color) !important;
        }
      }

      .p-menuitem-link.active-menuitem {
        background-color: var(--primary-50) !important;

        .menu-item-text {
          color: var(--primary-color) !important;
        }

        .menu-item-arrow {
          color: var(--primary-color) !important;
        }
      }
    }
    :deep(.p-menubar) {
      border: none;
      border-radius: 0;
      background: transparent;
      padding: 0.5rem 2rem;
      margin-left: 1rem;

      .p-menubar-root-list > .p-menuitem:first-child {
        margin-left: 1rem;
      }

      .p-menuitem {
        .p-menuitem-link.active-menuitem {
          background-color: var(--primary-50) !important;

          .menu-item-text {
            color: var(--primary-color) !important;
          }

          .menu-item-arrow {
            color: var(--primary-color) !important;
          }
        }
      }

      .p-submenu-list {
        .p-menuitem {
          .p-menuitem-link.active-menuitem {
            background-color: var(--primary-50) !important;

            .menu-item-text {
              color: var(--primary-color) !important;
            }

            .menu-item-arrow {
              color: var(--primary-color) !important;
            }
          }
        }
      }
    }

    :deep(.p-menuitem-link) {
      display: flex;
      align-items: center;
      padding: 0.75rem 1rem;
      height: 36px;

      .p-menuitem-icon {
        margin-right: 0.5rem;
      }
    }

    :deep(.p-submenu-list) {
      padding: 0.5rem;
      background: var(--surface-overlay);
      border: 1px solid var(--surface-border);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    :deep(.p-menubar-item) {
      &.active-menuitem {
        background-color: var(--primary-50) !important;

        .menu-item-text {
          color: var(--primary-color) !important;
          font-weight: bold;
        }
      }

      &[data-active='true'] {
        background-color: var(--primary-50) !important;

        .menu-item-text {
          color: var(--primary-color) !important;
          font-weight: bold;
        }
      }
    }
  }

  /* ===== 모바일 전용 오버라이드 ===== */
  /* 모바일 메뉴 스타일 추가 */
  @media screen and (max-width: 992px) {
    .top-menu.mobile-menu-active {
      :deep(.p-menubar) {
        .p-menubar-root-list {
          .p-menuitem {
            margin: 0.5rem 0;

            .p-menuitem-link {
              flex-wrap: nowrap;
              justify-content: flex-start;

              .menu-item-text {
                flex: 1;
              }

              .menu-item-arrow {
                margin-left: 0.5rem;
              }
            }
          }
        }
      }
    }
  }
</style>
